<script setup lang="ts">
import { sortBy } from "lodash-es";
import { ref, watch } from "vue";

import type { TeamMember } from "@/backend/http/userAdmin";
import { getTeamDescription, usersInTeam } from "@/backend/http/userAdmin";
import UserAvatar from "@/components/ui/UserAvatar/UserAvatar.vue";
import { useSyncedTask } from "@/composables/useSyncedTask";
import type { Team } from "@/model/session";

const props = defineProps<{ team: Team }>();

const members = ref<TeamMember[]>([]);
const description = ref("");
const { loading: loadingMembers, runSync: syncMembers } = useSyncedTask();
const { loading: loadingDescription, runSync: syncDescription } = useSyncedTask();

watch(
  () => props.team,
  (team) => {
    members.value = [];
    description.value = "";
    if (!team.id) return;

    void syncMembers(
      () => usersInTeam(team),
      (users) => (members.value = sortBy(users, (user) => user.name.toLowerCase())),
    );
    void syncDescription(
      () => getTeamDescription(team),
      (teamDescription) => (description.value = teamDescription),
    );
  },
  { immediate: true },
);
</script>

<template>
  <div class="team-overview">
    <div class="layout">
      <div class="info">
        <h3 class="title">{{ team.name }}</h3>
        <p v-if="description" class="description">{{ description }}</p>
        <p v-else-if="!loadingDescription" class="description empty">
          {{ $t("teamOverview.noDescription") }}
        </p>
      </div>
      <div class="team-members">
        <template v-if="members.length">
          <p class="count">{{ $t("teamOverview.count", members.length) }}</p>
          <ul class="members scrollable scrollable-relative">
            <li v-for="member in members" :key="member.id" class="member">
              <UserAvatar :user="member" />
              <span class="name">
                <span class="text">{{ member.name }}</span>
                <span v-if="member.isAdmin" class="badge">{{ $t("teamOverview.admin") }}</span>
              </span>
              <a class="email" :href="`mailto:${member.email}`">{{ member.email }}</a>
            </li>
          </ul>
        </template>
        <p v-else-if="!loadingMembers" class="empty">{{ $t("teamOverview.empty") }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/mixins/typography";
@use "@/styles/mixins/utils";
@use "@/styles/variables/a11y" as colors-a11y;
@use "@/styles/variables/colors";

$column-gap: 48px;

.team-overview {
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;

  .layout {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-height: 0;
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 24px 32px 12px;
  }

  .team-members {
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 0 32px 24px;
    gap: 8px;
  }

  @container (min-width: 720px) {
    .layout {
      flex-direction: row;
    }

    .info {
      flex: 0 1 min(35%, 600px);
      min-width: 0;
      padding: 0;
      gap: 0;
      border-right: solid 1px colors-a11y.$divider-color;
      overflow-y: auto;
    }

    .title,
    .description {
      padding: 12px 16px;
    }

    .title {
      border-bottom: 1px solid colors.$border-light;
    }

    .team-members {
      flex-grow: 1;
      min-width: 0;
      padding: 24px 32px;
    }
  }

  .title {
    @include typography.medium("bold");
  }

  .count,
  .empty {
    @include typography.small;

    color: colors.$text-subtle;
  }

  .description {
    @include typography.medium;

    white-space: pre-line;
  }

  .members {
    display: grid;
    grid-template-columns: repeat(
      auto-fill,
      minmax(max(320px, calc((100% - #{$column-gap}) / 2)), 1fr)
    );
    align-content: start;
    gap: 8px $column-gap;
  }

  .member {
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    column-gap: 12px;
    align-items: center;
    padding: 8px 0;
    border-bottom: solid 1px colors-a11y.$divider-color;

    .avatar {
      grid-row: span 2;
    }

    .name {
      @include typography.medium("semi-bold");

      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;

      .text {
        @include utils.ellipsis;
      }
    }

    .badge {
      @include typography.extra-small("semi-bold");

      flex-shrink: 0;
      margin-left: auto;
      padding: 2px 4px;
      border-radius: 4px;
      color: colors.$text-inverse;
      background-color: colors.$main;
    }

    .email {
      @include typography.small;
      @include utils.ellipsis;

      color: colors.$text-subtle;
    }
  }
}
</style>
